import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, User, X } from "lucide-react";
import { toast } from "sonner";
import { fetchAwayMap, awayFor, AwayBadge, fmtBack } from "@/components/AwayBadge";

let _staffCache = null;

export function StaffCombobox({ value, onChange, placeholder = "Assign to…", testid = "staff-combobox" }) {
  const [open, setOpen] = useState(false);
  const [staff, setStaff] = useState(_staffCache || []);
  const [away, setAway] = useState({});

  useEffect(() => {
    fetchAwayMap().then(setAway);
    if (_staffCache) return;
    api.get("/staff").then((r) => { _staffCache = r.data; setStaff(r.data); }).catch(() => {});
  }, []);
  useEffect(() => { if (open) fetchAwayMap().then(setAway); }, [open]);

  const current = value ? staff.find((s) => s.name === value) : null;
  const currentAway = current && awayFor(away, current.email);

  const pick = (s) => {
    const a = awayFor(away, s.email);
    if (a) {
      const hasBackup = a.backup_email && a.backup_email !== (s.email || "").toLowerCase();
      toast.warning(a.status === "away" ? `Heads up: ${s.name} is out of office until ${fmtBack(a)}` : `Heads up: ${s.name} is scheduled out soon (back ${fmtBack(a)})`, {
        id: `away-${s.email}`, duration: hasBackup ? 10000 : 5000,
        description: hasBackup ? `${a.backup_name} is covering for them.` : undefined,
        action: hasBackup ? { label: `Send to ${a.backup_name.split(" ")[0]} instead`, onClick: () => { onChange(a.backup_name, a.backup_email); toast.success(`Sent to ${a.backup_name} instead`); } } : undefined,
      });
    }
    onChange(s.name, s.email || "");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" data-testid={testid}
          className="w-full justify-between font-normal h-9">
          <span className="flex items-center gap-2 min-w-0 truncate">
            <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {value ? <span className="truncate">{value}</span> : <span className="text-muted-foreground">{placeholder}</span>}
            {currentAway && <AwayBadge away={currentAway} compact />}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-80" align="start">
        <Command>
          <CommandInput placeholder="Search staff…" data-testid={`${testid}-input`} />
          <CommandList>
            <CommandEmpty>No staff found.</CommandEmpty>
            <CommandGroup>
              {value && (
                <CommandItem value="unassign clear" onSelect={() => { onChange("", ""); setOpen(false); }} className="text-muted-foreground" data-testid={`${testid}-clear`}>
                  <X className="h-3.5 w-3.5 mr-2" /> Unassign
                </CommandItem>
              )}
              {staff.map((s) => {
                const a = awayFor(away, s.email);
                return (
                  <CommandItem key={s.id} value={`${s.name} ${s.email || ""}`} data-testid="staff-option" data-away={a ? a.status : undefined}
                    onSelect={() => pick(s)}>
                    <Check className={`h-3.5 w-3.5 mr-2 shrink-0 ${value === s.name ? "opacity-100 text-primary" : "opacity-0"}`} />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{s.name}</span>
                      {s.title && <span className="block text-xs text-muted-foreground truncate">{s.title}</span>}
                    </span>
                    {a && <AwayBadge away={a} compact className="ml-2" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
